import { useState, useCallback, useRef, useEffect, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import { motion, AnimatePresence } from "framer-motion";
import { initMercadoPago } from "@mercadopago/sdk-react";
import {
  Download, Upload, RotateCcw, Sparkles, Palette, ZoomIn, Clock, Trash2,
  ChevronDown, Image as ImageIcon, Loader2, LogIn, LogOut, User, CreditCard,
  Coins, Mail, Lock, Eye, EyeOff, X, Camera, Phone, Users, DollarSign,
  BarChart3, Shield, ArrowLeft, CameraIcon, MessageCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

// Init Mercado Pago
const MP_KEY = process.env.REACT_APP_MP_PUBLIC_KEY;
if (MP_KEY) initMercadoPago(MP_KEY, { locale: "pt-BR" });

const TYPES = [
  { id: "general", icon: Sparkles, title: "Restaurar Colorida", desc: "Rever riscos, manchas, rasgos e danos do tempo", img: "/images/btn-colorize.png" },
  { id: "colorization", icon: Palette, title: "Restaura Preto e Branco", desc: "Adicionar cores realistas em fotos preto e branco", img: "/images/btn-pb.png" },
  { id: "quality", icon: ZoomIn, title: "Melhorar Qualidade", desc: "Aumentar a nitidez, remover ruído e melhorar a definição", img: "/images/btn-quality.png" },
];
const PACKS = { pack_10: { credits: 10, price: 25, title: "10 Fotos" }, pack_25: { credits: 25, price: 50, title: "25 Fotos" } };

/* ─── Auth Context ─── */
const AuthCtx = createContext(null);
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const check = useCallback(async () => { try { const { data } = await api.get("/auth/me"); setUser(data); } catch { setUser(false); } finally { setLoading(false); } }, []);
  useEffect(() => { check(); }, [check]);
  const login = async (e, p) => { const { data } = await api.post("/auth/login", { email: e, password: p }); setUser(data); };
  const register = async (n, e, p, ph) => { const { data } = await api.post("/auth/register", { name: n, email: e, password: p, phone: ph }); setUser(data); };
  const logout = async () => { await api.post("/auth/logout"); setUser(false); };
  const refreshCredits = async () => { try { const { data } = await api.get("/credits"); setUser(u => u ? { ...u, credits: data.credits } : u); } catch {} };
  return <AuthCtx.Provider value={{ user, loading, login, register, logout, refreshCredits }}>{children}</AuthCtx.Provider>;
}
function useAuth() { return useContext(AuthCtx); }
function fmtErr(d) { if (!d) return "Algo deu errado."; if (typeof d === "string") return d; if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" "); return String(d); }

/* ─── PWA Install Banner ─── */
function PwaInstallBanner() {
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setPrompt(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", handler);
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(() => {});
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);
  if (!show) return null;
  return (
    <div className="pwa-banner" data-testid="pwa-install-banner">
      <div className="flex items-center gap-3">
        <img src="/images/logo-icon.png" alt="Fotomemo" className="w-10 h-10 rounded-xl" />
        <div>
          <p className="text-white font-semibold text-sm">Instalar Fotomemo</p>
          <p className="text-white/70 text-xs">Acesso rápido na tela inicial</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => setShow(false)} className="text-white/60 text-xs px-2 py-1">Depois</button>
        <button data-testid="pwa-install-button" onClick={async () => { if (prompt) { prompt.prompt(); await prompt.userChoice; } setShow(false); }}
          className="bg-white text-copper font-bold text-sm px-4 py-2 rounded-full">Instalar</button>
      </div>
    </div>
  );
}

/* ─── Auth Modal ─── */
function AuthModal({ onClose }) {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState(""); const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [phone, setPhone] = useState("");
  const [showPw, setShowPw] = useState(false); const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else { if (!name.trim()) { setError("Insira seu nome"); setBusy(false); return; } await register(name, email, password, phone); }
      onClose();
    } catch (err) { setError(fmtErr(err.response?.data?.detail) || err.message); } finally { setBusy(false); }
  };
  const inputCls = "w-full pl-10 pr-4 py-3 border border-border rounded-xl bg-dark3 text-cream focus:outline-none focus:ring-2 focus:ring-copper/40 focus:border-copper transition-all placeholder:text-cream_muted/50";
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="auth-modal">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-dark2 rounded-3xl w-full max-w-md p-8 relative shadow-2xl border border-border">
        <button onClick={onClose} data-testid="close-auth-modal" className="absolute top-4 right-4 text-cream_muted hover:text-cream"><X className="w-5 h-5" /></button>
        <div className="text-center mb-6">
          <img src="/images/logo-icon.png" alt="Fotomemo" className="w-16 h-16 mx-auto mb-3 rounded-2xl" />
          <h2 className="font-serif text-2xl font-bold text-cream">{mode === "login" ? "Entrar" : "Criar Conta"}</h2>
          <p className="text-sm text-cream_muted mt-1">{mode === "login" ? "Acesse sua conta" : "Ganhe 2 restaurações grátis!"}</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (<>
            <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream_muted" />
              <input data-testid="auth-name-input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Seu nome" className={inputCls} /></div>
            <div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream_muted" />
              <input data-testid="auth-phone-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Telefone (opcional)" className={inputCls} /></div>
          </>)}
          <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream_muted" />
            <input data-testid="auth-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="seu@email.com" className={inputCls} /></div>
          <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream_muted" />
            <input data-testid="auth-password-input" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required placeholder="Sua senha" minLength={6} className={`${inputCls} pr-10`} />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-cream_muted">{showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div>
          {error && <div className="bg-error/10 border border-error/30 rounded-xl p-3 text-sm text-error" data-testid="auth-error">{error}</div>}
          <button type="submit" disabled={busy} data-testid="auth-submit-button"
            className="w-full bg-copper text-white rounded-full py-3.5 font-semibold hover:bg-copper_hover transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 spinner-restore" /> : null} {mode === "login" ? "Entrar" : "Criar Conta"}
          </button>
        </form>
        <p className="text-center text-sm text-cream_muted mt-4">{mode === "login" ? "Não tem conta? " : "Já tem conta? "}
          <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="text-copper font-medium hover:underline">
            {mode === "login" ? "Criar conta grátis" : "Fazer login"}</button></p>
      </motion.div>
    </div>
  );
}

/* ─── Buy Credits Modal (PIX Inline + Cartão) ─── */
function BuyCreditsModal({ onClose }) {
  const [pack, setPack] = useState("pack_10");
  const [payMethod, setPayMethod] = useState("pix");
  const [loading, setLoading] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [polling, setPolling] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const { refreshCredits } = useAuth();
  const pollingRef = useRef(null);
  const p = PACKS[pack];

  // Cleanup polling on unmount
  useEffect(() => { return () => { if (pollingRef.current) clearInterval(pollingRef.current); }; }, []);

  const handlePix = async () => {
    setLoading(true); setError(""); setPixData(null);
    try {
      const { data } = await api.post("/payment/pix", { package_id: pack });
      setPixData(data);
      // Start polling
      setPolling(true);
      let attempts = 0;
      pollingRef.current = setInterval(async () => {
        attempts++;
        if (attempts > 120) { clearInterval(pollingRef.current); setPolling(false); return; }
        try {
          const { data: status } = await api.get(`/payment/pix/status/${data.payment_id}`);
          if (status.status === "approved") {
            clearInterval(pollingRef.current);
            setPolling(false);
            setPaymentDone(true);
            refreshCredits();
            setTimeout(() => onClose(), 3000);
          }
        } catch {}
      }, 3000);
    } catch (e) { setError(fmtErr(e.response?.data?.detail) || "Erro ao gerar PIX"); }
    finally { setLoading(false); }
  };

  const handleCard = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/payment/create-preference", { package_id: pack, origin_url: window.location.origin });
      if (data.init_point) window.location.href = data.init_point;
    } catch (e) { setError(fmtErr(e.response?.data?.detail) || "Erro"); }
    finally { setLoading(false); }
  };

  const copyPix = () => {
    if (pixData?.qr_code) {
      navigator.clipboard?.writeText(pixData.qr_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  if (paymentDone) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="payment-success-modal">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-dark2 rounded-3xl w-full max-w-md p-8 text-center shadow-2xl border border-border">
          <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-success" />
          </div>
          <h2 className="font-serif text-2xl font-bold text-cream mb-2">Pagamento Aprovado!</h2>
          <p className="text-cream_muted">{p.credits} créditos adicionados</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" data-testid="buy-credits-modal">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-dark2 rounded-3xl w-full max-w-lg p-6 sm:p-8 relative shadow-2xl border border-border my-8">
        <button onClick={() => { if (pollingRef.current) clearInterval(pollingRef.current); onClose(); }} data-testid="close-credits-modal"
          className="absolute top-4 right-4 text-cream_muted hover:text-cream z-10"><X className="w-5 h-5" /></button>
        <div className="text-center mb-6">
          <Coins className="w-12 h-12 text-copper mx-auto mb-3" />
          <h2 className="font-serif text-2xl font-bold text-cream">Comprar Créditos</h2>
        </div>

        {/* Package Selection */}
        {!pixData && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {Object.entries(PACKS).map(([id, pk]) => (
                <button key={id} onClick={() => { setPack(id); setError(""); }} data-testid={`select-${id}`}
                  className={`border rounded-2xl p-4 text-center transition-all ${pack === id ? "border-copper ring-2 ring-copper bg-copper/10" : "border-border hover:border-copper/40 bg-dark3"}`}>
                  <p className="font-serif text-lg font-bold text-cream">{pk.credits} fotos</p>
                  <p className="font-serif text-2xl font-bold text-copper">R$ {pk.price}</p>
                  <p className="text-xs text-cream_muted mt-1">R$ {(pk.price / pk.credits).toFixed(2)}/foto</p>
                </button>
              ))}
            </div>

            {/* Payment Method */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button onClick={() => setPayMethod("pix")}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium transition-all ${payMethod === "pix" ? "border-copper bg-copper/10 text-copper" : "border-border text-cream_muted hover:border-copper/40"}`}>
                <Phone className="w-4 h-4" /> PIX
              </button>
              <button onClick={() => setPayMethod("card")}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium transition-all ${payMethod === "card" ? "border-copper bg-copper/10 text-copper" : "border-border text-cream_muted hover:border-copper/40"}`}>
                <CreditCard className="w-4 h-4" /> Cartão
              </button>
            </div>

            {error && <div className="bg-error/10 border border-error/30 rounded-xl p-3 text-sm text-error mb-4">{error}</div>}

            <button onClick={payMethod === "pix" ? handlePix : handleCard} disabled={loading} data-testid="buy-credits-button"
              className="w-full bg-copper text-white rounded-full py-3.5 font-semibold hover:bg-copper_hover transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 spinner-restore" /> : payMethod === "pix" ? <Phone className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
              {payMethod === "pix" ? `Gerar PIX - R$ ${p.price},00` : `Pagar com Cartão - R$ ${p.price},00`}
            </button>
          </>
        )}

        {/* PIX QR Code Display */}
        {pixData && (
          <div className="space-y-4" data-testid="pix-payment-area">
            <div className="bg-dark3 border border-border rounded-2xl p-5 text-center">
              <p className="text-sm font-medium text-cream mb-3">Pague via PIX</p>
              <p className="text-2xl font-bold text-copper mb-4">R$ {pixData.valor},00</p>

              {/* QR Code Image */}
              {pixData.qr_code_base64 && (
                <div className="bg-white rounded-xl p-4 inline-block mb-4">
                  <img src={`data:image/png;base64,${pixData.qr_code_base64}`} alt="QR Code PIX" className="w-48 h-48 mx-auto" data-testid="pix-qr-image" />
                </div>
              )}

              {/* Copia e Cola */}
              <div className="bg-dark rounded-lg p-3 text-xs font-mono text-cream_muted break-all select-all mb-3 max-h-20 overflow-y-auto" data-testid="pix-code">
                {pixData.qr_code}
              </div>
              <button onClick={copyPix} data-testid="copy-pix-button"
                className={`w-full text-sm border py-2.5 rounded-xl transition-all font-medium ${copied ? "border-success text-success bg-success/10" : "border-copper/30 text-copper hover:bg-copper/10"}`}>
                {copied ? "Copiado!" : "Copiar código PIX"}
              </button>
            </div>

            {polling && (
              <div className="flex items-center justify-center gap-2 text-sm text-cream_muted">
                <Loader2 className="w-4 h-4 spinner-restore text-copper" />
                <span>Aguardando pagamento...</span>
              </div>
            )}

            <button onClick={() => { if (pollingRef.current) clearInterval(pollingRef.current); setPixData(null); setPolling(false); setError(""); }}
              className="w-full text-sm text-cream_muted hover:text-cream transition-colors text-center py-2">
              Voltar e escolher outro pacote
            </button>
          </div>
        )}

        <p className="text-xs text-cream_muted text-center mt-3">Pagamento seguro via Mercado Pago</p>
      </motion.div>
    </div>
  );
}

/* ─── Header ─── */
function Header({ onAuth, onCredits, onAdmin }) {
  const { user, logout } = useAuth();
  return (
    <header data-testid="app-header" className="sticky top-0 z-50 bg-dark/90 backdrop-blur-xl border-b border-border/40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex justify-between items-center h-16">
        <div className="flex items-center gap-2">
          <img src="/images/logo-icon.png" alt="Fotomemo" className="w-9 h-9 rounded-lg" />
          <img src="/images/logo-text.png" alt="Fotomemo" className="h-6 sm:h-7 object-contain" />
        </div>
        <div className="flex items-center gap-2">
          {user ? (<>
            <button onClick={onCredits} data-testid="credits-badge" className="flex items-center gap-1.5 bg-copper/20 text-copper rounded-full px-3 py-1.5 text-sm font-medium hover:bg-copper/30 transition-colors">
              <Coins className="w-4 h-4" /><span>{user.credits}</span></button>
            {user.role === "admin" && <button onClick={onAdmin} data-testid="admin-button" className="text-copper p-1.5 hover:bg-copper/10 rounded-lg transition-colors"><Shield className="w-4 h-4" /></button>}
            <button onClick={logout} data-testid="logout-button" className="text-cream_muted hover:text-error transition-colors p-1.5"><LogOut className="w-4 h-4" /></button>
          </>) : user === false ? (
            <button onClick={onAuth} data-testid="login-header-button" className="flex items-center gap-1.5 bg-copper text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-copper_hover transition-colors">
              <LogIn className="w-4 h-4" /> Entrar</button>
          ) : null}
        </div>
      </div>
    </header>
  );
}

/* ─── Hero ─── */
function HeroSection({ onScroll }) {
  return (
    <section className="relative overflow-hidden py-12 sm:py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <img src="/images/logo-text.png" alt="Fotomemo" className="h-10 sm:h-14 object-contain mb-4" />
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream leading-tight mb-4">Dê vida nova às suas memórias</h2>
            <p className="text-base text-cream_muted leading-relaxed mb-6 max-w-lg">
              Restaure fotos antigas, colorize imagens em preto e branco e melhore a qualidade — tudo com inteligência artificial.
            </p>
            <button data-testid="hero-cta-button" onClick={onScroll}
              className="bg-copper text-white rounded-full px-8 py-3.5 font-semibold hover:bg-copper_hover transition-all shadow-lg active:scale-95 flex items-center gap-2">
              Começar Restauração <ChevronDown className="w-4 h-4" />
            </button>
            <div className="mt-4 flex items-center gap-4 text-sm text-cream_muted">
              <span className="flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-copper" /> 2 grátis</span>
              <span className="text-border">|</span>
              <span className="flex items-center gap-1.5"><Coins className="w-4 h-4 text-copper" /> A partir de R$2/foto</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }}>
            <div className="rounded-3xl overflow-hidden border-2 border-border shadow-2xl" data-testid="hero-demo-slider">
              <img src="/images/capa.jpg" alt="Fotomemo - Antes e Depois" className="w-full" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── Type Cards ─── */
function TypeSelector({ selected, onSelect }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="type-selector-grid">
      {TYPES.map((t, i) => {
        const Icon = t.icon; const active = selected === t.id;
        return (
          <motion.button key={t.id} data-testid={`type-select-${t.id}`}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
            onClick={() => onSelect(t.id)}
            className={`bg-dark3 border rounded-2xl overflow-hidden shadow-sm transition-all hover:shadow-lg hover:-translate-y-1 cursor-pointer text-left ${active ? "border-copper ring-2 ring-copper" : "border-border hover:border-copper/40"}`}>
            <img src={t.img} alt={t.title} className="w-full h-36 sm:h-40 object-cover" />
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1"><Icon className={`w-4 h-4 ${active ? "text-copper" : "text-cream_muted"}`} />
                <h4 className="font-serif text-base font-semibold text-cream">{t.title}</h4></div>
              <p className="text-xs text-cream_muted">{t.desc}</p>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
}

/* ─── Upload with Camera ─── */
function UploadZone({ onFile, preview, isProcessing }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const onDrop = useCallback((a) => { if (a.length > 0) onFile(a[0]); }, [onFile]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { "image/*": [".jpg",".jpeg",".png",".webp"] }, maxSize: 20*1024*1024, multiple: false, disabled: isProcessing, noClick: true,
  });
  if (preview) {
    return (
      <div className="relative rounded-2xl overflow-hidden border-2 border-border bg-dark3" data-testid="upload-preview">
        <img src={preview} alt="Foto" className="w-full max-h-[350px] object-contain bg-dark" />
        {!isProcessing && <button onClick={() => onFile(null)} data-testid="remove-photo-button"
          className="absolute top-3 right-3 bg-dark/80 backdrop-blur rounded-full p-2 shadow-md hover:bg-dark"><RotateCcw className="w-4 h-4 text-cream" /></button>}
      </div>
    );
  }
  return (
    <div {...getRootProps()} data-testid="upload-zone"
      className={`w-full border-2 border-dashed rounded-2xl p-6 sm:p-10 flex flex-col items-center justify-center bg-dark3 transition-all ${isDragActive ? "upload-zone-active border-copper" : "border-border"}`}>
      <input {...getInputProps()} />
      {/* Hidden inputs for camera and gallery */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" style={{ display: "none" }} onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); }} />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); }} />
      <Upload className="w-10 h-10 text-cream_muted mb-3" />
      <p className="text-cream font-medium mb-1">Envie sua foto</p>
      <p className="text-sm text-cream_muted mb-4">JPG, PNG ou WebP • Até 20MB</p>
      <div className="flex gap-3">
        <button data-testid="camera-button" onClick={() => cameraRef.current?.click()}
          className="bg-copper text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-copper_hover transition-all active:scale-95 flex items-center gap-2">
          <CameraIcon className="w-4 h-4" /> Câmera
        </button>
        <button data-testid="gallery-button" onClick={() => fileRef.current?.click()}
          className="bg-dark2 text-cream border border-border rounded-full px-5 py-2.5 text-sm font-medium hover:bg-surface transition-all active:scale-95 flex items-center gap-2">
          <ImageIcon className="w-4 h-4" /> Galeria
        </button>
      </div>
    </div>
  );
}

/* ─── Processing ─── */
function Processing() {
  const msgs = ["Analisando danos...", "Reconstruindo detalhes...", "Aplicando IA...", "Finalizando..."];
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI(p => (p+1)%msgs.length), 3500); return () => clearInterval(t); }, [msgs.length]);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-dark3 rounded-2xl border border-border p-8 text-center" data-testid="processing-state">
      <Loader2 className="w-12 h-12 text-copper mx-auto mb-4 spinner-restore" />
      <h3 className="font-serif text-xl text-cream mb-2">Restaurando...</h3>
      <p className="text-sm text-cream_muted mb-4">{msgs[i]}</p>
      <div className="w-full max-w-xs mx-auto h-2 bg-dark rounded-full overflow-hidden"><div className="h-full bg-copper rounded-full progress-bar-animate" /></div>
    </motion.div>
  );
}

/* ─── Result ─── */
function Result({ orig, b64, mime, desc, onReset, onDl }) {
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} data-testid="result-section">
      <div className="rounded-2xl overflow-hidden border-2 border-border shadow-2xl max-h-[500px] mb-4" data-testid="compare-slider">
        <ReactCompareSlider
          itemOne={<ReactCompareSliderImage src={orig} alt="Original" style={{ objectFit: "contain", background: "#1A1A1A" }} />}
          itemTwo={<ReactCompareSliderImage src={`data:${mime};base64,${b64}`} alt="Restaurada" style={{ objectFit: "contain", background: "#1A1A1A" }} />}
          position={50} style={{ width: "100%", height: "100%", maxHeight: "500px" }} />
      </div>
      <div className="flex items-center justify-center gap-2 mb-4 text-xs text-cream_muted">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-cream_muted/30" /> Original</span>
        <span className="text-border mx-1">|</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-copper" /> Restaurada</span>
      </div>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <button data-testid="download-button" onClick={onDl} className="bg-copper text-white rounded-full px-8 py-3 font-medium hover:bg-copper_hover transition-all active:scale-95 flex items-center gap-2 w-full sm:w-auto">
          <Download className="w-4 h-4" /> Baixar JPG</button>
        <button data-testid="restore-again-button" onClick={onReset} className="border border-border text-cream rounded-full px-8 py-3 font-medium hover:bg-dark3 transition-all active:scale-95 flex items-center gap-2 w-full sm:w-auto">
          <RotateCcw className="w-4 h-4" /> Restaurar Outra</button>
      </div>
    </motion.div>
  );
}

/* ─── History ─── */
function History({ items, onDel }) {
  if (!items?.length) return <div className="text-center py-10" data-testid="history-empty"><ImageIcon className="w-10 h-10 text-cream_muted/20 mx-auto mb-2" /><p className="text-sm text-cream_muted">Nenhuma restauração ainda</p></div>;
  const labels = { general: "Restauração", colorization: "Colorização", quality: "Qualidade" };

  const handleDownload = async (id) => {
    try {
      const resp = await api.get(`/history/download/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `fotomemo-${id}.jpg`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* file not available */ }
  };

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="history-grid">
      {items.map(it => (
        <motion.div key={it.id} data-testid="history-item" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="bg-dark3 rounded-xl border border-border overflow-hidden group relative">
          <div className="aspect-square bg-dark flex items-center justify-center overflow-hidden cursor-pointer"
            onClick={() => it.has_file && handleDownload(it.id)}>
            {it.restored_thumbnail?.length > 100 ?
              <img src={`data:image/jpeg;base64,${it.restored_thumbnail}`} alt="thumb" className="w-full h-full object-cover" />
              : <ImageIcon className="w-6 h-6 text-cream_muted/20" />}
            {it.has_file && (
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Download className="w-6 h-6 text-white" />
              </div>
            )}
          </div>
          <div className="p-2">
            <span className="text-[9px] tracking-wider uppercase font-semibold text-copper">{labels[it.restoration_type] || it.restoration_type}</span>
            <p className="text-[10px] text-cream_muted">{new Date(it.created_at).toLocaleDateString("pt-BR")}</p>
          </div>
          {it.has_file && (
            <button onClick={() => handleDownload(it.id)} data-testid={`download-history-${it.id}`}
              className="absolute top-1 left-1 bg-dark/80 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Download className="w-3 h-3 text-copper" />
            </button>
          )}
          <button onClick={() => onDel(it.id)} className="absolute top-1 right-1 bg-dark/80 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3 text-error" /></button>
        </motion.div>
      ))}
    </div>
  );
}

/* ─── Admin Panel ─── */
function AdminPanel({ onClose }) {
  const [customers, setCustomers] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const load = async () => {
      try {
        const [c, r] = await Promise.all([api.get("/admin/customers"), api.get("/admin/revenue")]);
        setCustomers(c.data); setRevenue(r.data);
      } catch {} finally { setLoading(false); }
    }; load();
  }, []);
  if (loading) return <div className="fixed inset-0 z-[100] bg-dark flex items-center justify-center"><Loader2 className="w-8 h-8 text-copper spinner-restore" /></div>;
  return (
    <div className="fixed inset-0 z-[100] bg-dark overflow-y-auto" data-testid="admin-panel">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3"><Shield className="w-5 h-5 text-copper" /><h2 className="font-serif text-2xl text-cream">Admin</h2></div>
          <button onClick={onClose} data-testid="close-admin" className="text-cream_muted hover:text-cream flex items-center gap-1 text-sm"><ArrowLeft className="w-4 h-4" /> Voltar</button>
        </div>
        {/* Revenue Cards */}
        {revenue && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
            {[
              { label: "Hoje", val: `R$ ${revenue.day.total.toFixed(2)}`, sub: `${revenue.day.count} vendas` },
              { label: "Mês", val: `R$ ${revenue.month.total.toFixed(2)}`, sub: `${revenue.month.count} vendas` },
              { label: "Ano", val: `R$ ${revenue.year.total.toFixed(2)}`, sub: `${revenue.year.count} vendas` },
              { label: "Usuários", val: revenue.total_users, sub: "cadastrados" },
              { label: "Restaurações", val: revenue.total_restorations, sub: "realizadas" },
            ].map((c, i) => (
              <div key={i} className="bg-dark3 border border-border rounded-xl p-4">
                <p className="text-xs text-cream_muted uppercase tracking-wider mb-1">{c.label}</p>
                <p className="font-serif text-xl font-bold text-copper">{c.val}</p>
                <p className="text-xs text-cream_muted">{c.sub}</p>
              </div>
            ))}
          </div>
        )}
        {/* Customers Table */}
        <h3 className="font-serif text-lg text-cream mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-copper" /> Clientes</h3>
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm" data-testid="admin-customers-table">
            <thead className="bg-dark3"><tr className="text-cream_muted text-xs uppercase tracking-wider">
              <th className="px-4 py-3 text-left">Nome</th><th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Telefone</th><th className="px-4 py-3 text-right">Créditos</th>
              <th className="px-4 py-3 text-right">Investido</th><th className="px-4 py-3 text-left">Data</th>
            </tr></thead>
            <tbody>
              {customers.map((c, i) => (
                <tr key={i} className="border-t border-border hover:bg-dark3/50 transition-colors">
                  <td className="px-4 py-3 text-cream">{c.name || "-"}</td>
                  <td className="px-4 py-3 text-cream_muted">{c.email}</td>
                  <td className="px-4 py-3 text-cream_muted">{c.phone || "-"}</td>
                  <td className="px-4 py-3 text-right text-copper font-medium">{c.credits}</td>
                  <td className="px-4 py-3 text-right text-cream">R$ {(c.total_spent || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-cream_muted text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString("pt-BR") : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─── Main ─── */
function AppContent() {
  const { user, loading, refreshCredits } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [selType, setSelType] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const appRef = useRef(null);
  const step2Ref = useRef(null);
  const step3Ref = useRef(null);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const pay = p.get("payment"), ref = p.get("ref");
    if (pay === "success" && ref) { pollRef(ref); window.history.replaceState({}, "", window.location.pathname); }
    else if (pay) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const pollRef = async (ref, n = 0) => {
    if (n >= 10) return;
    try { const { data } = await api.get(`/payment/check/${ref}`); if (data.status === "approved") { refreshCredits(); return; } } catch {}
    setTimeout(() => pollRef(ref, n + 1), 2000);
  };

  useEffect(() => { if (user) fetchH(); }, [user]);
  const fetchH = async () => { try { const { data } = await api.get("/history"); setHistory(data); } catch {} };

  const handleFile = (f) => { if (!f) { setFile(null); setPreview(null); setResult(null); setError(null); return; }
    setFile(f); setResult(null); setError(null);
    const r = new FileReader(); r.onload = e => setPreview(e.target.result); r.readAsDataURL(f); };

  const restore = async () => {
    if (!user) { setShowAuth(true); return; }
    if (user.credits <= 0) { setShowCredits(true); return; }
    if (!file || !selType) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const r = new FileReader();
      const { b64, mime } = await new Promise(res => { r.onload = e => { const d = e.target.result; res({ b64: d.split(",")[1], mime: d.split(";")[0].split(":")[1] }); }; r.readAsDataURL(file); });
      const { data } = await api.post("/restore", { image_base64: b64, image_mime_type: mime, restoration_type: selType }, { timeout: 120000 });
      setResult(data); refreshCredits(); fetchH();
      setTimeout(() => step3Ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    } catch (e) {
      const m = e.response?.data?.detail || "Erro na restauração.";
      if (e.response?.status === 402) setShowCredits(true);
      setError(typeof m === "string" ? m : JSON.stringify(m));
    } finally { setBusy(false); }
  };

  const dl = () => { if (!result) return; const a = document.createElement("a");
    a.href = `data:${result.restored_mime_type};base64,${result.restored_image_base64}`;
    a.download = `fotomemo-${Date.now()}.jpg`; a.click(); };

  const reset = () => { setFile(null); setPreview(null); setResult(null); setError(null); setSelType(null); };
  const delH = async (id) => { try { await api.delete(`/history/${id}`); setHistory(h => h.filter(x => x.id !== id)); } catch {} };

  if (loading) return <div className="min-h-screen bg-dark flex items-center justify-center"><Loader2 className="w-8 h-8 text-copper spinner-restore" /></div>;

  return (
    <div className="min-h-screen bg-dark">
      <Header onAuth={() => setShowAuth(true)} onCredits={() => setShowCredits(true)} onAdmin={() => setShowAdmin(true)} />
      <HeroSection onScroll={() => appRef.current?.scrollIntoView({ behavior: "smooth" })} />

      <section ref={appRef} className="py-12 sm:py-16" data-testid="app-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="mb-10">
            <h3 className="font-serif text-xl sm:text-2xl text-cream mb-4 flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-copper text-white flex items-center justify-center text-xs font-bold">1</span> Escolha uma das opções</h3>
            <TypeSelector selected={selType} onSelect={(t) => { setSelType(t); setTimeout(() => step2Ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 200); }} />
          </div>

          <AnimatePresence>
            {selType && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mb-10 overflow-hidden">
                <div ref={step2Ref}>
                <h3 className="font-serif text-xl sm:text-2xl text-cream mb-4 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-copper text-white flex items-center justify-center text-xs font-bold">2</span> Envie sua foto</h3>
                {!user && (
                  <div className="bg-copper/10 border border-copper/30 rounded-2xl p-5 text-center mb-4" data-testid="login-prompt">
                    <p className="text-sm text-cream mb-3">Faça login para restaurar. <strong className="text-copper">2 grátis!</strong></p>
                    <button onClick={() => setShowAuth(true)} data-testid="login-prompt-button"
                      className="bg-copper text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-copper_hover transition-all active:scale-95 inline-flex items-center gap-2">
                      <LogIn className="w-4 h-4" /> Entrar / Criar Conta</button>
                  </div>
                )}
                {user && user.credits <= 0 && !result && (
                  <div className="bg-copper/10 border border-copper/30 rounded-2xl p-5 text-center mb-4" data-testid="no-credits-banner">
                    <Coins className="w-8 h-8 text-copper mx-auto mb-2" />
                    <p className="text-cream mb-3">Créditos esgotados</p>
                    <button onClick={() => setShowCredits(true)} className="bg-copper text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-copper_hover inline-flex items-center gap-2">
                      <CreditCard className="w-4 h-4" /> Comprar Créditos</button>
                  </div>
                )}
                {user && <UploadZone onFile={handleFile} preview={preview} isProcessing={busy} />}
                {error && <div className="bg-error/10 border border-error/30 rounded-xl p-3 mt-3 text-sm text-error">{error}</div>}
                {!busy && !result && preview && user?.credits > 0 && (
                  <div className="mt-5 text-center">
                    <button data-testid="restore-submit-button" onClick={restore}
                      className="bg-copper text-white rounded-full px-10 py-3.5 font-semibold hover:bg-copper_hover transition-all active:scale-95 inline-flex items-center gap-2 shadow-lg">
                      <Sparkles className="w-5 h-5" /> Restaurar Foto</button>
                    <p className="text-xs text-cream_muted mt-2">1 crédito será consumido</p>
                  </div>
                )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>{busy && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mb-10"><Processing /></motion.div>}</AnimatePresence>
          <AnimatePresence>
            {result && !busy && (
              <motion.div ref={step3Ref} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mb-10">
                <h3 className="font-serif text-xl text-cream mb-4 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-success text-white flex items-center justify-center text-xs font-bold">3</span> Resultado</h3>
                <Result orig={preview} b64={result.restored_image_base64} mime={result.restored_mime_type} desc={result.description} onReset={reset} onDl={dl} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {user && (
        <section className="py-12 bg-dark2" data-testid="history-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-lg text-cream flex items-center gap-2"><Clock className="w-4 h-4 text-copper" /> Histórico</h3>
              <button onClick={fetchH} className="text-xs text-cream_muted hover:text-copper flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Atualizar</button>
            </div>
            <History items={history} onDel={delH} />
          </div>
        </section>
      )}

      {!user && (
        <section className="py-12 bg-dark2" data-testid="pricing-section">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <h3 className="font-serif text-2xl text-cream mb-4">Simples e Acessível</h3>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(PACKS).map(([id, pk]) => (
                <div key={id} className="bg-dark3 rounded-2xl border border-border p-5">
                  <p className="font-serif text-lg font-bold text-cream">{pk.title}</p>
                  <p className="font-serif text-3xl font-bold text-copper my-2">R$ {pk.price}</p>
                  <p className="text-xs text-cream_muted">R$ {(pk.price/pk.credits).toFixed(2)}/foto</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-copper font-medium mt-3">+ 2 restaurações grátis ao criar conta!</p>
          </div>
        </section>
      )}

      <footer className="py-8 border-t border-border" data-testid="app-footer">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <a href="https://wa.me/+5554981125628?text=Preciso%20de%20suporte%20para%20o%20App%20Fotomemo..."
            target="_blank" rel="noopener noreferrer" data-testid="whatsapp-support-button"
            className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-6 py-3 font-medium hover:bg-[#1DA851] transition-all active:scale-95 shadow-lg mb-4">
            <MessageCircle className="w-5 h-5" /> Suporte via WhatsApp
          </a>
          <p className="text-xs text-cream_muted">Fotomemo &mdash; fotomemo.com.br</p>
        </div>
      </footer>

      <PwaInstallBanner />
      <AnimatePresence>
        {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
        {showCredits && <BuyCreditsModal onClose={() => setShowCredits(false)} />}
        {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}
      </AnimatePresence>
    </div>
  );
}

function App() { return <AuthProvider><AppContent /></AuthProvider>; }
export default App;
